import { useEffect, useRef, useState } from "react";
import { Palette, Check } from "lucide-react";
import { APPEARANCE_MODES, useAppearance } from "@/context/AppearanceContext";

/**
 * Compact atmospheric appearance selector.
 * Sits near the Ni² icon in the title bar.
 * Popover with four circular wallpaper-style thumbnails.
 */
export default function AppearanceSelector() {
  const { mode, setMode } = useAppearance();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const activeMode = APPEARANCE_MODES.find((m) => m.id === mode) || APPEARANCE_MODES[0];

  return (
    <div ref={rootRef} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="nn-icon-btn"
        aria-label="Appearance"
        title="Appearance"
        data-testid="appearance-btn"
      >
        <Palette className="h-3.5 w-3.5" strokeWidth={1.6} />
      </button>

      {open && (
        <div
          className="absolute right-0 top-[calc(100%+8px)] z-40 nn-enter"
          role="dialog"
          aria-label="Appearance"
        >
          <div
            className="p-3"
            style={{
              width: 296,
              borderRadius: 18,
              background:
                "linear-gradient(180deg, rgba(30, 40, 60, 0.86), rgba(14, 20, 34, 0.9))",
              border: "1px solid rgba(190, 210, 240, 0.18)",
              boxShadow:
                "inset 0 1px 0 rgba(255,255,255,0.09), 0 30px 90px -20px rgba(0,0,0,0.7)",
              backdropFilter: "blur(28px) saturate(150%)",
              WebkitBackdropFilter: "blur(28px) saturate(150%)",
            }}
          >
            <div className="mb-2 flex items-center justify-between px-1">
              <div className="nn-eyebrow">Appearance</div>
              <div className="text-[10.5px] font-mono text-[hsl(var(--nn-ink-faint))]">
                {activeMode.label}
              </div>
            </div>

            <div className="grid grid-cols-4 gap-2">
              {APPEARANCE_MODES.map((m) => (
                <button
                  key={m.id}
                  onClick={() => setMode(m.id)}
                  className="group flex flex-col items-center gap-1.5 rounded-xl p-1.5 transition-colors hover:bg-white/[0.04]"
                  data-testid={`appearance-${m.id}`}
                  aria-pressed={mode === m.id}
                  title={m.label}
                >
                  <span
                    className="relative grid h-12 w-12 place-items-center rounded-full"
                    style={{
                      background: m.swatch,
                      boxShadow:
                        mode === m.id
                          ? "0 0 0 2px rgba(160, 200, 255, 0.85), 0 0 0 4px rgba(140, 180, 250, 0.16), inset 0 0 0 1px rgba(255,255,255,0.14)"
                          : "0 0 0 1px rgba(255,255,255,0.12), inset 0 0 0 1px rgba(255,255,255,0.10)",
                      transition: "box-shadow 200ms ease, transform 200ms ease",
                      transform: mode === m.id ? "scale(1.02)" : "scale(1)",
                    }}
                  >
                    {mode === m.id && (
                      <span
                        className="grid h-5 w-5 place-items-center rounded-full"
                        style={{
                          background: "rgba(14, 20, 34, 0.85)",
                          boxShadow: "0 0 0 1px rgba(255,255,255,0.25)",
                        }}
                      >
                        <Check
                          className="h-3 w-3 text-white"
                          strokeWidth={2.4}
                        />
                      </span>
                    )}
                  </span>
                  <span
                    className="text-[10px] font-medium leading-tight text-center"
                    style={{
                      color:
                        mode === m.id
                          ? "hsl(var(--nn-ink))"
                          : "hsl(var(--nn-ink-muted))",
                    }}
                  >
                    {m.label}
                  </span>
                </button>
              ))}
            </div>

            <div className="mt-3 flex items-start gap-2 rounded-lg border border-white/[0.06] bg-white/[0.03] px-2.5 py-2">
              <Palette
                className="mt-0.5 h-3 w-3 shrink-0 text-[hsl(var(--nn-ink-muted))]"
                strokeWidth={1.6}
              />
              <div className="text-[10.5px] leading-[15px] text-[hsl(var(--nn-ink-muted))]">
                {activeMode.description}. Layout, contrast and typography are
                preserved across all modes.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
